import { useState, type FormEvent } from "react";
import { PlanPriceEditor } from "./PlanPriceEditor";
type Row = Record<string, any>;
type Api = <T>(
  path: string,
  options?: { method?: string; body?: unknown; csrf?: string | undefined },
) => Promise<T>;

export function PlansPanel({
  plans,
  api,
  csrf,
  onSaved,
}: {
  plans: Row[];
  api: Api;
  csrf: string | undefined;
  onSaved: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  async function save(event: FormEvent<HTMLFormElement>, plan: Row) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await persist(plan, {
      name: form.get("name"),
      description: form.get("description"),
      kind: form.get("kind"),
      unit: form.get("unit"),
      monthlyPrice: form.get("monthlyPrice"),
      active: form.get("active") === "on",
      version: Number(plan.version),
      features: String(form.get("features") ?? "")
        .split("\n")
        .map((value) => value.trim())
        .filter(Boolean),
    });
  }
  async function persist(plan: Row, body: unknown) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api(`/admin/plans/${plan.id}`, {
        method: "POST",
        csrf,
        body,
      });
      setEditing(null);
      setNotice(
        "Plan saved. New selections use this price. Existing subscriptions and complimentary accounts are unchanged; earlier setup quotes must be reviewed again.",
      );
      try {
        await onSaved();
      } catch {
        setError(
          "The price was saved, but the list could not refresh. Reload this page before editing again.",
        );
      }
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <p className="cr-page-intro">
        Change monthly prices here and save immediately. No deployment is
        needed. New selections use the saved rate; existing subscriptions and
        complimentary accounts keep their agreed pricing. Review inclusions
        before offering a plan.
      </p>
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="cr-notice" role="status">
          {notice}
        </p>
      )}
      {!plans.length && (
        <section className="cr-panel">
          <h2>No plans yet</h2>
          <p>
            Add the Home Phone, Business Phone, Business Trunk and MozartCX
            Enterprise drafts to start.
          </p>
          <button
            className="cr-button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                await api("/admin/plans/templates", {
                  method: "POST",
                  csrf,
                  body: {},
                });
                await onSaved();
              } catch (cause) {
                setError((cause as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Add starter plans
          </button>
        </section>
      )}
      {plans.map((plan) => (
        <section className="cr-panel" key={plan.id}>
          <div className="cr-account-heading">
            <div>
              <h2>{plan.name}</h2>
              <p>
                {plan.active ? "Available" : "Not offered"} ·{" "}
                {plan.price_configured
                  ? `${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(plan.recurring_minor) / 100)} / ${plan.unit === "seat" ? "user / month" : "month"}`
                  : "Price not set"}
              </p>
            </div>
            <button
              className="cr-button secondary"
              disabled={busy}
              aria-expanded={editing === plan.id}
              onClick={() => setEditing(editing === plan.id ? null : plan.id)}
            >
              {editing === plan.id ? "Cancel editing" : "Edit plan details"}
            </button>
          </div>
          {editing !== plan.id ? (
            <>
              <PlanPriceEditor
                key={`${plan.id}:${plan.version}`}
                plan={plan}
                busy={busy}
                onSave={(body) => persist(plan, body)}
              />
              <p>{plan.description}</p>
              {plan.features?.length > 0 && (
                <ul>
                  {plan.features.map((feature: string, index: number) => (
                    <li key={index}>
                      {typeof feature === "string"
                        ? feature
                        : JSON.stringify(feature)}
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <form className="cr-form" onSubmit={(event) => save(event, plan)}>
              <fieldset className="cr-form-fields" disabled={busy}>
                <div className="cr-form-grid">
                  <label className="cr-field">
                    <span>Plan name</span>
                    <input
                      name="name"
                      required
                      maxLength={120}
                      defaultValue={plan.name}
                    />
                  </label>
                  <label className="cr-field">
                    <span>Customer type</span>
                    <select name="kind" defaultValue={plan.kind}>
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="enterprise">Enterprise · MozartCX</option>
                    </select>
                  </label>
                  <label className="cr-field">
                    <span>Monthly price (USD)</span>
                    <input
                      name="monthlyPrice"
                      inputMode="decimal"
                      pattern="[0-9]{1,6}(\.[0-9]{1,2})?"
                      defaultValue={
                        plan.price_configured
                          ? (Number(plan.recurring_minor) / 100).toFixed(2)
                          : ""
                      }
                      aria-describedby={`price-help-${plan.id}`}
                    />
                    <small id={`price-help-${plan.id}`}>
                      Leave blank until pricing is decided.
                    </small>
                  </label>
                  <label className="cr-field">
                    <span>Price applies to</span>
                    <select name="unit" defaultValue={plan.unit}>
                      <option value="month">Whole plan, per month</option>
                      <option value="seat">Each user, per month</option>
                    </select>
                  </label>
                </div>
                <label className="cr-field">
                  <span>Description</span>
                  <textarea
                    name="description"
                    rows={3}
                    required
                    maxLength={2000}
                    defaultValue={plan.description}
                  />
                </label>
                <label className="cr-field">
                  <span>Included features, one per line</span>
                  <textarea
                    name="features"
                    rows={4}
                    defaultValue={(plan.features ?? []).join("\n")}
                  />
                </label>
                <label className="cr-check">
                  <input
                    name="active"
                    type="checkbox"
                    defaultChecked={plan.active}
                  />
                  <span>
                    Make this plan available. I have reviewed the price and
                    inclusions.
                  </span>
                </label>
                <button className="cr-button">
                  {busy ? "Saving…" : "Save plan"}
                </button>
              </fieldset>
            </form>
          )}
        </section>
      ))}
      <p className="cr-page-intro">
        Starting-rate references, reviewed September 8, 2026:{" "}
        <a
          href="https://www.1-voip.com/residential-voip.php"
          target="_blank"
          rel="noreferrer"
        >
          1-VoIP residential
        </a>{" "}
        and{" "}
        <a
          href="https://www.ooma.com/small-business-phone-systems/plans/"
          target="_blank"
          rel="noreferrer"
        >
          Ooma business
        </a>
        . These are Cache River recommendations, not a regulated or universal
        industry rate.
      </p>
    </>
  );
}
